// Tiny auth helper. Wraps Supabase's session state in a hook that returns
// the bits the rest of the app actually uses: the user's UUID, email, and
// whether they're an admin.
//
// Admin = JWT app_metadata.role === 'admin'. Set in Supabase Dashboard →
// Authentication → Users → click user → "Raw app meta data". Mirrors the
// veritas.is_admin() Postgres function exactly so frontend gating and
// backend RLS stay in sync.

import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { User } from '@supabase/supabase-js';

export type CurrentUser = {
  id: string;
  email: string | null;
  isAdmin: boolean;
};

function readUser(u: User | null): CurrentUser | null {
  if (!u) return null;
  const role = (u.app_metadata as { role?: string } | null)?.role;
  return {
    id: u.id,
    email: u.email ?? null,
    isAdmin: role === 'admin',
  };
}

// Subscribes to auth state changes, returns the current user (or null
// while loading / signed out). Each component that calls this gets its
// own subscription, but the cost is trivial — Supabase JS dedupes
// internally and the callback is just a state setter.
export function useCurrentUser(): { user: CurrentUser | null; loading: boolean } {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubbed = false;
    supabase.auth.getUser().then(({ data }) => {
      if (unsubbed) return;
      setUser(readUser(data.user));
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(readUser(session?.user ?? null));
      setLoading(false);
    });
    return () => {
      unsubbed = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { user, loading };
}
